import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import Swal from 'sweetalert2';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUICustomDataGrid } from '../../../../../atomic/molecules';
import { SCUIButton } from '../../../../../atomic/atoms';
import TimeHelper from '../../../../../utils/time';
import Helper from '../../../../../utils/helper';
import RefundRestQuery from '../../../../../services/query/refundRest/RefundRestQuery.services';
import { enumPaymentMethodStatus } from '../../../../../types/enums/status';

export const SCUIRefundDetailPaymentInfo: FC<{
    payBackListInfo?: any;
    currencySymbol?: any;
    refundId?: number | string;
    customerId?: number | string;
}> = ({ payBackListInfo, currencySymbol, refundId, customerId }) => {
    const { t } = useTranslation();
    const [downloadingRefundDetailId, setDownloadingRefundDetailId] = useState<number | null>(null);
    const datas = payBackListInfo && payBackListInfo.items.filter((item: any) => item.refundDetailStatusId === 2);

    const handleRefundReceiptClick = async (row: any) => {
        if (refundId == null || customerId == null) {
            Swal.fire(t('refund.receiptNotFound'), '', 'error');
            return;
        }

        const resolvedRefundId: string | number = refundId;
        const resolvedCustomerId: string | number = customerId;

        setDownloadingRefundDetailId(row.refundDetailId);
        try {
            const response = await RefundRestQuery.GetRefundReceiptPdf({
                refundId: resolvedRefundId,
                customerId: resolvedCustomerId,
            });
            if (response && response.size > 0 && response.type === 'application/pdf') {
                const pdfUrl = URL.createObjectURL(response);
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `RefundReceipt_${resolvedRefundId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(pdfUrl);
            } else {
                Swal.fire(t('refund.receiptNotFound'), '', 'error');
            }
        } catch (error) {
            console.error('Refund receipt PDF download error:', error);
            Swal.fire(t('refund.receiptNotFound'), '', 'error');
        } finally {
            setDownloadingRefundDetailId(null);
        }
    };

    const columns: ExtendedGridColDef[] = [
        { field: 'refundDetailId', headerName: t('refund.paymentId'), width: 90, minWidth: 80 },
        {
            field: 'consolidatedOrderId',
            headerName: t('refund.consolidatedOrderId'),
            width: 100,
            minWidth: 90,
        },
        { field: 'referenceNo', headerName: t('refund.referenceNo'), width: 110, minWidth: 100 },
        { field: 'authCode', headerName: t('refund.authCode'), width: 100, minWidth: 90 },
        { field: 'transactionId', headerName: t('refund.transactionId'), width: 120, minWidth: 110 },
        {
            field: 'refundMethodId',
            headerName: t('refund.paymentMethod'),
            width: 120,
            minWidth: 110,
            renderCell: (params) => {
                return <div>{Helper.getPaymentName(params.row.refundMethodId, params.row.refundWalletType)}</div>;
            },
        },
        {
            field: 'amountRefunded',
            headerName: t('refund.amount'),
            width: 110,
            minWidth: 100,
            renderCell: (params) => {
                return (
                    <div>
                        {Helper.formatNumber(params.row.amountRefunded)} {currencySymbol}
                    </div>
                );
            },
        },
        {
            field: 'dateRefunded',
            headerName: t('refund.paymentDate'),
            width: 170,
            minWidth: 160,
            renderCell: (params) => {
                return <div>{TimeHelper.formatDateHourSecond(params.row.updateDate)}</div>;
            },
        },
        {
            field: 'statusName',
            headerName: t('general.status'),
            width: 140,
            minWidth: 130,
            renderCell: (params) => {
                return <div>{Helper.getRefundStatusName(params.row.refundDetailStatusId)}</div>;
            },
        },
        {
            field: 'description',
            headerName: t('general.description'),
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                return (
                    <Tooltip title={params.row.description || ''}>
                        <div
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                            }}
                        >
                            {params.row.description}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            field: 'refundReceipt',
            headerName: t('refund.refundInfoForm'),
            width: 160,
            minWidth: 150,
            sortable: false,
            renderCell: (params) => {
                const canCreate = params.row.refundMethodId === enumPaymentMethodStatus.CreditCard;

                return (
                    <SCUIButton
                        type="secondary"
                        sizeType="default"
                        size="s"
                        text={t('refund.createReceipt')}
                        disabled={!canCreate || downloadingRefundDetailId === params.row.refundDetailId}
                        onClick={() => handleRefundReceiptClick(params.row)}
                    />
                );
            },
        },
    ];

    return (
        <React.Fragment>
            <SCUICustomDataGrid randomId rows={datas} columns={columns} />
        </React.Fragment>
    );
};
